import { useEffect, useMemo, useRef, useState } from 'react';
import { PharmacySpecialtyT } from '@lotushealth/lotushealth_js_sdk/schemas';

import type { RxPharmacy } from '@/entities/rx';
import {
  patientPharmacyToRxPharmacy,
  pharmacyToRxPharmacy,
  useAddPatientPharmacyMutation,
  usePatientPharmaciesQuery,
  useSearchPharmaciesQuery,
} from '@/entities/rx';

import type { RxFormApi } from './types';
import { chooseDefaultPatientPharmacy } from './chooseDefaultPatientPharmacy';

interface SearchFields {
  name: string;
  city: string;
  state: string;
  zip: string;
  specialty: PharmacySpecialtyT[];
}

export interface PharmacySearchResultRow {
  pharmacyId: number;
  label: string;
  location: string | null;
  fallbackRxPharmacy: RxPharmacy;
}

export interface SavedPharmacyRow {
  pharmacyId: number;
  rxPharmacy: RxPharmacy;
  isPrimary: boolean;
}

export const PHARMACY_SPECIALTY_LABEL_BY_VALUE: Partial<Record<PharmacySpecialtyT, string>> = {
  [PharmacySpecialtyT.EPCS]: 'EPCS',
  [PharmacySpecialtyT.FaxPharmacy]: 'Fax Pharmacy',
  [PharmacySpecialtyT.TwentyFourHourPharmacy]: '24 Hour Pharmacy',
  [PharmacySpecialtyT.LongTermCarePharmacy]: 'Long-Term Care Pharmacy',
  [PharmacySpecialtyT.MailOrder]: 'Mail Order',
  [PharmacySpecialtyT.Retail]: 'Retail',
  [PharmacySpecialtyT.SpecialtyPharmacy]: 'Specialty Pharmacy',
};

function getPharmacySpecialtyMatchValues(specialty: PharmacySpecialtyT): string[] {
  const label = PHARMACY_SPECIALTY_LABEL_BY_VALUE[specialty];
  if (!label || label === specialty) return [specialty];
  // PharmacySpecialties is a passthrough string[] from DoseSpot, so accept both
  // the SDK enum value and the display label without fuzzy string matching.
  return [specialty, label];
}

function matchesSelectedSpecialties(pharmacy: RxPharmacy, selected: PharmacySpecialtyT[]): boolean {
  if (selected.length === 0) return true;
  const returned = new Set(pharmacy.PharmacySpecialties ?? []);
  // OR semantics: a pharmacy passes the filter if it carries *any* of the
  // selected specialties. AND would require every chip to apply to the same
  // pharmacy simultaneously, which is impossible for mutually-exclusive
  // categories like Retail vs. Mail Order, and is also not the conventional
  // expectation for chip-style multi-select filters.
  return selected.some((specialty) =>
    getPharmacySpecialtyMatchValues(specialty).some((value) => returned.has(value))
  );
}

export interface UsePharmacySelectionResult {
  patientPharmaciesQuery: ReturnType<typeof usePatientPharmaciesQuery>;
  addPatientPharmacyMutation: ReturnType<typeof useAddPatientPharmacyMutation>;

  savedPharmacies: SavedPharmacyRow[];
  savedPharmaciesById: ReadonlyMap<string, SavedPharmacyRow>;

  isSearchOpen: boolean;
  openSearch: () => void;
  closeSearch: () => void;

  searchFields: SearchFields;
  setSearchFields: (next: SearchFields) => void;

  pharmacySearchQuery: ReturnType<typeof useSearchPharmaciesQuery>;
  searchResults: PharmacySearchResultRow[];

  pendingAddId: number | null;
  addError: string | null;
  addFromSearchResult: (pharmacy: PharmacySearchResultRow) => Promise<void>;

  pendingSetPrimaryId: number | null;
  setPrimaryError: string | null;
  setAsPrimary: (pharmacyId: number) => Promise<void>;
}

export function usePharmacySelection(userId: string, form: RxFormApi): UsePharmacySelectionResult {
  const patientPharmaciesQuery = usePatientPharmaciesQuery(userId);
  const addPatientPharmacyMutation = useAddPatientPharmacyMutation();
  const hasPrepopulatedRef = useRef(false);

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchFields, setSearchFields] = useState<SearchFields>({
    name: '',
    city: '',
    state: '',
    zip: '',
    specialty: [],
  });
  const [addError, setAddError] = useState<string | null>(null);
  const [pendingAddId, setPendingAddId] = useState<number | null>(null);
  const [pendingSetPrimaryId, setPendingSetPrimaryId] = useState<number | null>(null);
  const [setPrimaryError, setSetPrimaryError] = useState<string | null>(null);

  const pharmacySearchInput = useMemo(() => {
    const name = searchFields.name.trim();
    const city = searchFields.city.trim();
    const state = searchFields.state.trim().toUpperCase();
    const zip = searchFields.zip.trim();

    const input = {
      name: name.length >= 3 ? name : null,
      city: city.length >= 3 ? city : null,
      state: state.length === 2 ? state : null,
      zip: zip.length >= 3 ? zip : null,
      pageNumber: 1,
    };

    const hasSearchCriterion = Boolean(input.name || input.city || input.zip);

    if (!hasSearchCriterion) return undefined;

    return input;
  }, [searchFields.city, searchFields.name, searchFields.state, searchFields.zip]);

  const pharmacySearchQuery = useSearchPharmaciesQuery(pharmacySearchInput);

  useEffect(() => {
    if (hasPrepopulatedRef.current) return;
    const saved = patientPharmaciesQuery.data;
    if (!saved) return;

    // Only prepopulate when empty (never override clinician selection).
    if (form.state.values.selectedPharmacy) {
      hasPrepopulatedRef.current = true;
      return;
    }

    const chosen = chooseDefaultPatientPharmacy(saved);
    const rxPharmacy = chosen ? patientPharmacyToRxPharmacy(chosen) : null;
    if (rxPharmacy) {
      form.setFieldValue('selectedPharmacy', rxPharmacy);
    }

    // Idempotent: run exactly once when pharmacies first become available.
    hasPrepopulatedRef.current = true;
  }, [form, patientPharmaciesQuery.data]);

  const savedPharmacies = useMemo((): SavedPharmacyRow[] => {
    return (patientPharmaciesQuery.data ?? [])
      .map((p) => {
        const rxPharmacy = patientPharmacyToRxPharmacy(p);
        const pharmacyId = rxPharmacy?.PharmacyId;
        if (!rxPharmacy || pharmacyId == null || !Number.isFinite(pharmacyId)) return null;
        return { pharmacyId, rxPharmacy, isPrimary: p.IsDefault === true };
      })
      .filter((p): p is SavedPharmacyRow => p != null);
  }, [patientPharmaciesQuery.data]);

  const savedPharmaciesById = useMemo(() => {
    return new Map(savedPharmacies.map((p) => [String(p.pharmacyId), p] as const));
  }, [savedPharmacies]);

  const searchResults = useMemo((): PharmacySearchResultRow[] => {
    const items = pharmacySearchQuery.data?.Items ?? [];
    return items
      .map((p) => {
        const pharmacyId = p.PharmacyId;
        if (pharmacyId == null || !Number.isFinite(pharmacyId)) return null;

        const label = p.StoreName ?? `Pharmacy ${pharmacyId}`;
        const location = [p.City, p.State, p.ZipCode]
          .filter((v): v is string => Boolean(v && v.trim()))
          .join(', ');
        const fallbackRxPharmacy = pharmacyToRxPharmacy(p);
        if (!fallbackRxPharmacy) return null;
        if (!matchesSelectedSpecialties(fallbackRxPharmacy, searchFields.specialty)) return null;

        return {
          pharmacyId,
          label,
          location: location || null,
          fallbackRxPharmacy,
        };
      })
      .filter((r): r is PharmacySearchResultRow => r != null);
  }, [pharmacySearchQuery.data, searchFields.specialty]);

  const addFromSearchResult = async (pharmacy: PharmacySearchResultRow) => {
    setAddError(null);

    try {
      setPendingAddId(pharmacy.pharmacyId);
      await addPatientPharmacyMutation.mutateAsync({
        userId,
        data: { pharmacyId: pharmacy.pharmacyId },
      });

      const refetchResult = await patientPharmaciesQuery.refetch();
      const saved = refetchResult.data ?? [];
      const savedMatch = saved.find((p) => p.PharmacyId === pharmacy.pharmacyId) ?? null;
      const rxPharmacy = savedMatch
        ? patientPharmacyToRxPharmacy(savedMatch)
        : pharmacy.fallbackRxPharmacy;

      if (rxPharmacy) {
        form.setFieldValue('selectedPharmacy', rxPharmacy);
      }

      setIsSearchOpen(false);
    } catch {
      setAddError('Could not add pharmacy. Please try again.');
    } finally {
      setPendingAddId(null);
    }
  };

  const setAsPrimary = async (pharmacyId: number) => {
    setSetPrimaryError(null);

    try {
      setPendingSetPrimaryId(pharmacyId);
      await addPatientPharmacyMutation.mutateAsync({
        userId,
        data: { pharmacyId, setAsPrimary: true },
      });
      await patientPharmaciesQuery.refetch();
    } catch {
      setSetPrimaryError('Could not set pharmacy as primary. Please try again.');
    } finally {
      setPendingSetPrimaryId(null);
    }
  };

  return {
    patientPharmaciesQuery,
    addPatientPharmacyMutation,
    savedPharmacies,
    savedPharmaciesById,
    isSearchOpen,
    openSearch: () => setIsSearchOpen(true),
    closeSearch: () => setIsSearchOpen(false),
    searchFields,
    setSearchFields,
    pharmacySearchQuery,
    searchResults,
    pendingAddId,
    addError,
    addFromSearchResult,
    pendingSetPrimaryId,
    setPrimaryError,
    setAsPrimary,
  };
}
